
import { useState, useEffect } from "react";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API = "http://gipl-backend.onrender.com/api/v1";

const apiFetch = async (method, path, body, token) => {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
};

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  gold: "#C9A84C", goldLight: "#E8C97A",
  navy: "#0A1628", navyLight: "#1E3459",
  cream: "#F8F4ED",
  success: "#22C55E", warning: "#F59E0B",
  danger: "#EF4444", info: "#3B82F6",
};

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
const Badge = ({ text, type = "default" }) => {
  const colors = {
    success: { bg: "#dcfce7", color: "#15803d" },
    warning: { bg: "#fef9c3", color: "#a16207" },
    danger:  { bg: "#fee2e2", color: "#b91c1c" },
    info:    { bg: "#dbeafe", color: "#1d4ed8" },
    default: { bg: "#f1f5f9", color: "#64748b" },
  };
  const s = colors[type] || colors.default;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 600,
      padding: "3px 10px", borderRadius: 20,
    }}>{text}</span>
  );
};

const PolicyCard = ({ policy, type, onClaim }) => {
  const [expanded, setExpanded] = useState(false);

  const statusType = (s) => {
    if (s === "Active") return "success";
    if (s === "Due Soon") return "warning";
    if (["Expired","Lapsed"].includes(s)) return "danger";
    return "default";
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
  const fmtAmt = (n) => n ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

  const colors = { life: C.gold, health: C.info, general: C.success };
  const icons  = { life: "💼", health: "🏥", general: "🚗" };

  return (
    <div style={{
      background: "#fff", borderRadius: 16,
      border: `1px solid #e8e2d6`,
      borderLeft: `4px solid ${colors[type]}`,
      marginBottom: 12, overflow: "hidden",
      transition: "box-shadow 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      {/* Header */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}>
        <div style={{
          width: 44, height: 44, borderRadius: "50%",
          background: `${colors[type]}20`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, flexShrink: 0,
        }}>{icons[type]}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.navy, marginBottom: 2 }}>
            {policy.plan_name || policy.policy_type || policy.policy_nature}
          </div>
          <div style={{ fontSize: 12, color: "#718096" }}>
            {policy.policy_number} · {policy.insurance_companies?.company_name || "—"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <Badge text={policy.policy_status} type={statusType(policy.policy_status)} />
          <div style={{ fontSize: 11, color: "#718096", marginTop: 4 }}>
            {expanded ? "▲ Less" : "▼ Details"}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div style={{ padding: "0 20px 14px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
        {type === "life" && <>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Sum Assured</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{fmtAmt(policy.sum_assured)}</div>
          </div>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Annual Premium</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{fmtAmt(policy.annual_premium)}</div>
          </div>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Next Due</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.warning }}>{fmt(policy.next_premium_due_date)}</div>
          </div>
        </>}
        {type === "health" && <>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Sum Insured</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{fmtAmt(policy.sum_insured)}</div>
          </div>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Premium</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{fmtAmt(policy.annual_premium)}</div>
          </div>
          <div style={{ background: policy.policy_status==="Due Soon" ? "#fff3cd" : "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Expires</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: policy.policy_status==="Due Soon" ? C.warning : C.navy }}>{fmt(policy.policy_expiry_date)}</div>
          </div>
        </>}
        {type === "general" && <>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Sum Insured</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{fmtAmt(policy.sum_insured)}</div>
          </div>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Vehicle / Property</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{policy.vehicle_registration || policy.policy_nature}</div>
          </div>
          <div style={{ background: "#f8f4ed", borderRadius: 8, padding: "8px 12px" }}>
            <div style={{ fontSize: 10, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>Expires</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.warning }}>{fmt(policy.policy_expiry_date)}</div>
          </div>
        </>}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ borderTop: "1px solid #f1ebe0", padding: "14px 20px", background: "#fdfaf5" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginBottom: 14 }}>
            {[
              ["Policy Number", policy.policy_number],
              ["Company", policy.insurance_companies?.company_name],
              ["Issue Date", fmt(policy.policy_issue_date)],
              ...(type==="life" ? [
                ["Maturity Date", fmt(policy.policy_maturity_date)],
                ["Nominee", policy.nominee_name || "—"],
                ["Nominee Relation", policy.nominee_relation || "—"],
              ] : []),
              ...(type==="health" ? [
                ["TPA", policy.tpa_name || "—"],
                ["Cashless", policy.cashless_available ? "Yes" : "No"],
              ] : []),
              ...(type==="general" && policy.vehicle_make ? [
                ["Vehicle", `${policy.vehicle_make} ${policy.vehicle_model}`],
                ["Year", policy.vehicle_year || "—"],
              ] : []),
            ].map(([k,v]) => v ? (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0" }}>
                <span style={{ color: "#718096" }}>{k}</span>
                <span style={{ color: C.navy, fontWeight: 500 }}>{v}</span>
              </div>
            ) : null)}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{
              background: "#fff", border: "1px solid #e8e2d6",
              color: "#4a5568", padding: "6px 14px", borderRadius: 8,
              fontSize: 12, cursor: "pointer",
            }}>📄 View Policy Document</button>
            {["health","general"].includes(type) && (
              <button onClick={() => onClaim(policy, type)}
                style={{
                  background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`,
                  color: C.gold, border: "none",
                  padding: "6px 14px", borderRadius: 8,
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}>🏥 Raise Claim</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── CLAIM FORM ───────────────────────────────────────────────────────────────
const ClaimForm = ({ policy, type, token, customerId, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    hospital_name: "", admission_date: "", discharge_date: "",
    diagnosis: "", claimed_amount: "",
    incident_date: "", incident_location: "", incident_description: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState("");

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg("");
    try {
      const endpoint = type === "health" ? `/health/${policy.policy_id}/claims` : `/general/${policy.policy_id}/claims`;
      const payload  = {
        customer_id:    customerId,
        claimed_amount: parseFloat(form.claimed_amount),
        ...(type === "health" ? {
          hospital_name:  form.hospital_name,
          admission_date: form.admission_date || null,
          discharge_date: form.discharge_date || null,
          diagnosis:      form.diagnosis,
          claim_type:     "Reimbursement",
        } : {
          incident_date:        form.incident_date || null,
          incident_location:    form.incident_location,
          incident_description: form.incident_description,
        }),
      };
      const data = await apiFetch("POST", endpoint, payload, token);
      if (data.success) {
        setMsg("✅ Claim raised successfully! GIPL team will contact you within 24 hours.");
        setTimeout(() => { onSuccess(); onClose(); }, 2500);
      } else { setMsg("❌ " + (data.message || "Failed to raise claim")); }
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(10,22,40,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 20, width: "100%", maxWidth: 560,
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`,
          padding: "20px 24px", borderRadius: "20px 20px 0 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: C.gold }}>
              🏥 Raise a Claim
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
              Policy: {policy.policy_number}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.1)", border: "none",
            color: "#fff", width: 32, height: 32, borderRadius: "50%",
            cursor: "pointer", fontSize: 16,
          }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          {type === "health" ? (<>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Hospital Name <span style={{ color:C.danger }}>*</span></label>
                <input value={form.hospital_name} onChange={e => set("hospital_name")(e.target.value)} required
                  placeholder="Name of the hospital"
                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Diagnosis</label>
                <input value={form.diagnosis} onChange={e => set("diagnosis")(e.target.value)}
                  placeholder="Medical condition / diagnosis"
                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Admission Date</label>
                <input type="date" value={form.admission_date} onChange={e => set("admission_date")(e.target.value)}
                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Discharge Date</label>
                <input type="date" value={form.discharge_date} onChange={e => set("discharge_date")(e.target.value)}
                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
              </div>
            </div>
          </>) : (<>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Incident Date</label>
                <input type="date" value={form.incident_date} onChange={e => set("incident_date")(e.target.value)}
                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Location</label>
                <input value={form.incident_location} onChange={e => set("incident_location")(e.target.value)}
                  placeholder="Where did the incident occur?"
                  style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Incident Description</label>
              <textarea value={form.incident_description} onChange={e => set("incident_description")(e.target.value)}
                placeholder="Describe what happened..."
                style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", minHeight:80, resize:"vertical" }} />
            </div>
          </>)}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.04em" }}>Claimed Amount (₹) <span style={{ color:C.danger }}>*</span></label>
            <input type="number" value={form.claimed_amount} onChange={e => set("claimed_amount")(e.target.value)} required min="0"
              placeholder="Total amount being claimed"
              style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
          </div>

          {msg && <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:16, fontSize:13, background: msg.startsWith("✅") ? "#f0fdf4" : "#fff5f5", color: msg.startsWith("✅") ? "#15803d" : C.danger, border:`1px solid ${msg.startsWith("✅")?"#bbf7d0":"#fecaca"}` }}>{msg}</div>}

          <div style={{ display: "flex", gap: 10 }}>
            <button type="submit" disabled={saving} style={{
              background: `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`,
              color: C.gold, border:"none", padding:"11px 24px",
              borderRadius:10, fontSize:13, fontWeight:600,
              cursor: saving?"not-allowed":"pointer", fontFamily:"inherit",
            }}>{saving ? "⏳ Submitting..." : "Submit Claim"}</button>
            <button type="button" onClick={onClose} style={{
              background:"#f8f4ed", border:"1px solid #e8e2d6",
              color:"#4a5568", padding:"11px 20px", borderRadius:10,
              fontSize:13, cursor:"pointer", fontFamily:"inherit",
            }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── LOGIN PAGE (Mobile OTP) ──────────────────────────────────────────────────
const PortalLogin = ({ onLogin }) => {
  const [step, setStep]         = useState("mobile"); // mobile | otp
  const [mobile, setMobile]     = useState("");
  const [otp, setOtp]           = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");

  const sendOtp = async (e) => {
    e.preventDefault();
    if (mobile.length < 10) { setError("Enter a valid 10-digit mobile number"); return; }
    setLoading(true); setError("");
    try {
      // Simulate OTP — in production, call SMS gateway API
      const mockOtp = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedOtp(mockOtp);
      setStep("otp");
      // Show OTP in alert for demo (remove in production)
      setTimeout(() => alert(`📱 Demo OTP for ${mobile}: ${mockOtp}\n\n(In production this would be sent via SMS)`), 100);
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (otp !== generatedOtp) {
        setError("Invalid OTP. Please try again.");
        setLoading(false); return;
      }
      // Find customer by mobile in the backend
      const data = await apiFetch("GET", `/customers?search=${encodeURIComponent(mobile)}&limit=1`, null, null);
      // For portal we do a special customer login
      // Using the staff login API but checking by mobile
      // In production: implement a dedicated customer login endpoint
      if (data.success && data.data && data.data.length > 0) {
        const customer = data.data[0];
        onLogin({ customer, mobile });
      } else {
        // Try login as a staff user who might have this mobile
        // For demo: use admin token with restricted view
        const loginData = await apiFetch("POST", "/auth/login", { email: "admin@gipl.in", password: "Admin@123" }, null);
        if (loginData.success) {
          // Find customer by mobile
          const custData = await apiFetch("GET", `/customers?search=${encodeURIComponent(mobile)}&limit=1`, null, loginData.token);
          if (custData.success && custData.data && custData.data.length > 0) {
            onLogin({ customer: custData.data[0], mobile, adminToken: loginData.token });
          } else {
            setError("No account found with this mobile number. Please contact GIPL office.");
          }
        }
      }
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 50%, #2a4a7a 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans', sans-serif", padding: 20,
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* Background pattern */}
      <div style={{ position: "fixed", inset: 0, opacity: 0.03, backgroundImage: "radial-gradient(circle, #C9A84C 1px, transparent 1px)", backgroundSize: "30px 30px", pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 420, position: "relative" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 72, height: 72,
            background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
            borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px",
            fontFamily: "'Playfair Display',serif", fontSize: 32, fontWeight: 700, color: C.navy,
            boxShadow: "0 8px 32px rgba(201,168,76,0.3)",
          }}>G</div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: "#fff" }}>GIPL Insurance</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 4, letterSpacing: "0.1em", textTransform: "uppercase" }}>Customer Self-Service Portal</div>
        </div>

        {/* Card */}
        <div style={{
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(20px)",
          borderRadius: 24, padding: 32,
          boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
        }}>
          {step === "mobile" ? (
            <form onSubmit={sendOtp}>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display',serif", marginBottom: 6 }}>Welcome back</div>
                <div style={{ fontSize: 13, color: "#718096" }}>Enter your registered mobile number to view your policies</div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#718096", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.04em" }}>Mobile Number</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ background:"#f8f4ed", border:"1px solid #e8e2d6", borderRadius:8, padding:"10px 12px", fontSize:13, color:"#4a5568", flexShrink:0 }}>🇮🇳 +91</div>
                  <input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))}
                    placeholder="10-digit mobile number" required maxLength={10}
                    style={{ flex:1, padding:"10px 14px", border:"1px solid #e8e2d6", borderRadius:8, fontSize:14, fontFamily:"inherit", outline:"none", background:"#fdfaf5", letterSpacing:"2px" }} />
                </div>
              </div>
              {error && <div style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:8, padding:"8px 12px", fontSize:13, color:C.danger, marginBottom:12 }}>{error}</div>}
              <button type="submit" disabled={loading || mobile.length < 10} style={{
                width:"100%", padding:"13px",
                background: mobile.length === 10
                  ? `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`
                  : "#d1d5db",
                color: mobile.length === 10 ? C.gold : "#9ca3af",
                border:"none", borderRadius:10, fontSize:15, fontWeight:600,
                cursor: mobile.length === 10 ? "pointer" : "not-allowed",
                fontFamily:"inherit", letterSpacing:"0.02em",
              }}>
                {loading ? "Sending OTP..." : "Send OTP →"}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp}>
              <div style={{ marginBottom: 24 }}>
                <button type="button" onClick={() => { setStep("mobile"); setOtp(""); setError(""); }}
                  style={{ background:"none", border:"none", color:C.gold, fontSize:13, cursor:"pointer", marginBottom:8, padding:0, fontFamily:"inherit" }}>
                  ← Change number
                </button>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display',serif", marginBottom: 6 }}>Enter OTP</div>
                <div style={{ fontSize: 13, color: "#718096" }}>6-digit OTP sent to <strong>+91 {mobile}</strong></div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="• • • • • •" maxLength={6} required
                  style={{
                    width:"100%", padding:"14px", border:"2px solid #e8e2d6",
                    borderRadius:10, fontSize:22, fontFamily:"inherit",
                    outline:"none", boxSizing:"border-box",
                    textAlign:"center", letterSpacing:"12px", background:"#fdfaf5",
                  }}
                  onFocus={e => e.target.style.borderColor = C.gold}
                  onBlur={e => e.target.style.borderColor = "#e8e2d6"}
                />
              </div>
              {error && <div style={{ background:"#fff5f5", border:"1px solid #fecaca", borderRadius:8, padding:"8px 12px", fontSize:13, color:C.danger, marginBottom:12 }}>{error}</div>}
              <button type="submit" disabled={loading || otp.length < 6} style={{
                width:"100%", padding:"13px",
                background: otp.length === 6
                  ? `linear-gradient(135deg, ${C.navy}, ${C.navyLight})`
                  : "#d1d5db",
                color: otp.length === 6 ? C.gold : "#9ca3af",
                border:"none", borderRadius:10, fontSize:15, fontWeight:600,
                cursor: otp.length === 6 ? "pointer" : "not-allowed",
                fontFamily:"inherit",
              }}>
                {loading ? "Verifying..." : "Verify & Login"}
              </button>
              <div style={{ textAlign:"center", marginTop:14 }}>
                <button type="button" onClick={sendOtp}
                  style={{ background:"none", border:"none", color:C.info, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                  Resend OTP
                </button>
              </div>
            </form>
          )}
        </div>

        <div style={{ textAlign:"center", marginTop:16, fontSize:12, color:"rgba(255,255,255,0.35)" }}>
          IRDAI Registered Insurance Marketing Firm<br/>Guardian Insurance Partners Ltd — Vijayawada
        </div>
      </div>
    </div>
  );
};

// ─── MAIN PORTAL APP ──────────────────────────────────────────────────────────
export default function CustomerPortal() {
  const [session, setSession]     = useState(null);
  const [portfolio, setPortfolio] = useState({ life: [], health: [], general: [] });
  const [loading, setLoading]     = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [claimModal, setClaimModal] = useState(null);
  const [claimCount, setClaimCount] = useState(0);

  const loadPortfolio = async (s) => {
    if (!s || !s.customer) return;
    setLoading(true);
    try {
      const token = s.adminToken;
      const data  = await apiFetch("GET", `/customers/${s.customer.customer_id}/portfolio`, null, token);
      if (data.success) setPortfolio(data.data);
    } finally { setLoading(false); }
  };

  const handleLogin = (s) => {
    setSession(s);
    loadPortfolio(s);
  };

  const handleLogout = () => {
    setSession(null);
    setPortfolio({ life: [], health: [], general: [] });
  };

  if (!session) return <PortalLogin onLogin={handleLogin} />;

  const { customer } = session;
  const totalPolicies = portfolio.life.length + portfolio.health.length + portfolio.general.length;
  const totalPremium  = [...portfolio.life, ...portfolio.health, ...portfolio.general]
    .reduce((s,p) => s + parseFloat(p.annual_premium || 0), 0);

  const navItems = [
    { id: "overview",  icon: "🏠", label: "Overview" },
    { id: "life",      icon: "💼", label: `Life (${portfolio.life.length})` },
    { id: "health",    icon: "🏥", label: `Health (${portfolio.health.length})` },
    { id: "general",   icon: "🚗", label: `General (${portfolio.general.length})` },
    { id: "claims",    icon: "📋", label: `Claims ${claimCount > 0 ? `(${claimCount})` : ""}` },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f8f4ed", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* Claim Modal */}
      {claimModal && (
        <ClaimForm
          policy={claimModal.policy} type={claimModal.type}
          token={session.adminToken} customerId={customer.customer_id}
          onClose={() => setClaimModal(null)}
          onSuccess={() => { setClaimCount(c => c+1); setClaimModal(null); }}
        />
      )}

      {/* Header */}
      <div style={{ background: C.navy, padding: "0 24px", height: 64, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:36, height:36, background:`linear-gradient(135deg,${C.gold},${C.goldLight})`, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Playfair Display',serif", fontWeight:700, color:C.navy, fontSize:16 }}>G</div>
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#fff" }}>GIPL Insurance</div>
            <div style={{ fontSize:10, color:C.gold, letterSpacing:"0.1em", textTransform:"uppercase" }}>Customer Portal</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:13, fontWeight:600, color:"#fff" }}>{customer.full_name}</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>{customer.customer_code}</div>
          </div>
          <div style={{ width:36, height:36, borderRadius:"50%", background:`linear-gradient(135deg,${C.gold},${C.goldLight})`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Playfair Display',serif", fontWeight:700, color:C.navy, fontSize:14 }}>
            {customer.full_name?.charAt(0)}
          </div>
          <button onClick={handleLogout} style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"rgba(255,255,255,0.7)", padding:"6px 14px", borderRadius:8, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Sign Out</button>
        </div>
      </div>

      {/* Nav tabs */}
      <div style={{ background:"#fff", borderBottom:"1px solid #e8e2d6", padding:"0 24px", display:"flex", gap:0, overflowX:"auto" }}>
        {navItems.map(n => (
          <button key={n.id} onClick={() => setActiveTab(n.id)}
            style={{
              padding:"14px 20px", border:"none", background:"none",
              borderBottom: activeTab === n.id ? `2px solid ${C.gold}` : "2px solid transparent",
              color: activeTab === n.id ? C.navy : "#718096",
              fontWeight: activeTab === n.id ? 600 : 400,
              fontSize: 13, cursor:"pointer", fontFamily:"inherit",
              display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap",
            }}>
            {n.icon} {n.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ maxWidth: 900, margin:"0 auto", padding:"28px 24px" }}>

        {/* OVERVIEW */}
        {activeTab === "overview" && (
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:C.navy, marginBottom:6 }}>
              Welcome, {customer.full_name?.split(" ")[0]}! 👋
            </div>
            <div style={{ fontSize:13, color:"#718096", marginBottom:24 }}>
              Here is a summary of all your insurance policies with GIPL.
            </div>

            {/* KPI strip */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:24 }}>
              {[
                { icon:"📋", label:"Total Policies", value:totalPolicies, color:C.navy },
                { icon:"💰", label:"Annual Premium", value:`₹${totalPremium.toLocaleString("en-IN")}`, color:C.success },
                { icon:"📞", label:"GIPL Support", value:"1800-XXX-XXXX", color:C.info },
              ].map(k => (
                <div key={k.label} style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:"18px 20px" }}>
                  <div style={{ fontSize:22, marginBottom:8 }}>{k.icon}</div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, color:k.color, marginBottom:4 }}>{k.value}</div>
                  <div style={{ fontSize:12, color:"#718096" }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Recent policies */}
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:C.navy, marginBottom:12 }}>Your Policies</div>
            {loading ? <div style={{ textAlign:"center", padding:40, color:"#718096" }}>Loading your policies...</div> : (
              totalPolicies === 0 ? (
                <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:40, textAlign:"center" }}>
                  <div style={{ fontSize:40, marginBottom:10 }}>📭</div>
                  <div style={{ fontSize:15, fontWeight:600, color:C.navy, marginBottom:4 }}>No policies found</div>
                  <div style={{ fontSize:13, color:"#718096" }}>Contact GIPL office to add your existing policies to this portal.</div>
                </div>
              ) : <>
                {portfolio.life.slice(0,2).map(p => <PolicyCard key={p.policy_id} policy={p} type="life" onClaim={setClaimModal} />)}
                {portfolio.health.slice(0,2).map(p => <PolicyCard key={p.policy_id} policy={p} type="health" onClaim={(p,t) => setClaimModal({policy:p,type:t})} />)}
                {portfolio.general.slice(0,2).map(p => <PolicyCard key={p.policy_id} policy={p} type="general" onClaim={(p,t) => setClaimModal({policy:p,type:t})} />)}
              </>
            )}
          </div>
        )}

        {/* LIFE */}
        {activeTab === "life" && (
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.navy, marginBottom:16 }}>💼 Life Insurance Policies</div>
            {portfolio.life.length === 0
              ? <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:40, textAlign:"center" }}><div style={{ fontSize:36, marginBottom:8 }}>💼</div><div style={{ fontSize:14, color:"#718096" }}>No life insurance policies found</div></div>
              : portfolio.life.map(p => <PolicyCard key={p.policy_id} policy={p} type="life" onClaim={() => {}} />)}
          </div>
        )}

        {/* HEALTH */}
        {activeTab === "health" && (
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.navy, marginBottom:16 }}>🏥 Health Insurance Policies</div>
            {portfolio.health.length === 0
              ? <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:40, textAlign:"center" }}><div style={{ fontSize:36, marginBottom:8 }}>🏥</div><div style={{ fontSize:14, color:"#718096" }}>No health insurance policies found</div></div>
              : portfolio.health.map(p => <PolicyCard key={p.policy_id} policy={p} type="health" onClaim={(p,t) => setClaimModal({policy:p,type:t})} />)}
          </div>
        )}

        {/* GENERAL */}
        {activeTab === "general" && (
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.navy, marginBottom:16 }}>🚗 General Insurance Policies</div>
            {portfolio.general.length === 0
              ? <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:40, textAlign:"center" }}><div style={{ fontSize:36, marginBottom:8 }}>🚗</div><div style={{ fontSize:14, color:"#718096" }}>No general insurance policies found</div></div>
              : portfolio.general.map(p => <PolicyCard key={p.policy_id} policy={p} type="general" onClaim={(p,t) => setClaimModal({policy:p,type:t})} />)}
          </div>
        )}

        {/* CLAIMS */}
        {activeTab === "claims" && (
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:700, color:C.navy, marginBottom:8 }}>📋 Raise a Claim</div>
            <div style={{ fontSize:13, color:"#718096", marginBottom:20 }}>Click on any Health or General policy below and tap "Raise Claim"</div>
            {[...portfolio.health, ...portfolio.general].length === 0
              ? <div style={{ background:"#fff", border:"1px solid #e8e2d6", borderRadius:14, padding:40, textAlign:"center" }}><div style={{ fontSize:36, marginBottom:8 }}>📋</div><div style={{ fontSize:14, color:"#718096" }}>No claimable policies found</div></div>
              : <>
                {portfolio.health.map(p => <PolicyCard key={p.policy_id} policy={p} type="health" onClaim={(p,t) => setClaimModal({policy:p,type:t})} />)}
                {portfolio.general.map(p => <PolicyCard key={p.policy_id} policy={p} type="general" onClaim={(p,t) => setClaimModal({policy:p,type:t})} />)}
              </>}
          </div>
        )}

      </div>

      {/* Footer */}
      <div style={{ background:C.navy, padding:"16px 24px", marginTop:40, textAlign:"center" }}>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>
          Guardian Insurance Partners Ltd (GIPL) · IRDAI Registered IMF · Vijayawada, Andhra Pradesh
        </div>
      </div>
    </div>
  );
}
